// v2 house — a half-timbered cottage facing the street: a limewashed plaster body
// in a dark oak frame (corner posts, a sill band, diagonal braces), a steep
// street-facing gable roof with real eaves and a two-step plaster attic gable, a
// shuttered window, a little post porch over the door and a tall brick chimney.
// Ported 1:1 from age-of-mcp's v2/house.ts, using the library's shared
// `foundationSlab` and `village` (PLASTER, TIMBER, buildScaffold, gableRoof,
// shutterWindow) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, village, smokePlume, EFFECT_LOOP, type Part } from "../../lib";

const BASE  = 0.14;          // top of the foundation slab
const W     = 0.72;          // body width (x)
const D     = 0.68;          // body depth (z)
const DOOR  = "#4a3018";
const BRICK = "#8a4a38";

// The "hearth is lit" cue: a small smoke plume off the brick chimney (its cap
// tops out at y = 1.33 on the finished cottage).
const plume = smokePlume([0.22, 1.33, -0.16], { rise: 0.32, radius: 0.05 });

// The plaster body in its oak frame, `h` tall on the foundation.
function frame(h: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(W, h, D), color: village.PLASTER,
      position: [0, BASE + h / 2, 0] },
  ];
  for (const [x, z] of [[-W / 2, -D / 2], [W / 2, -D / 2], [-W / 2, D / 2], [W / 2, D / 2]] as const) {
    out.push({ geometry: new THREE.BoxGeometry(0.07, h, 0.07), color: village.TIMBER,
      position: [x, BASE + h / 2, z] });
  }
  if (h > 0.5) {
    // Sill band ringing the body at knee height.
    out.push({ geometry: new THREE.BoxGeometry(W + 0.02, 0.05, D + 0.02), color: village.TIMBER,
      position: [0, 0.30, 0] });
  }
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.86)];
  if (s === 1) return [foundationSlab(0.86), ...frame(0.3), ...village.buildScaffold(0.86, 0.82, 0.62)];
  if (s === 2) return [foundationSlab(0.86), ...frame(0.6), ...village.buildScaffold(0.86, 0.82, 1.1)];

  const wallH = 0.6;
  const top   = BASE + wallH;          // 0.74
  const rise  = 0.46;
  const front = D / 2;                 // 0.34
  return [
    foundationSlab(0.86),
    ...frame(wallH),
    // Diagonal oak braces low on the front face.
    { geometry: new THREE.BoxGeometry(0.05, 0.26, 0.03), color: village.TIMBER,
      position: [-0.32, 0.30, front + 0.005], rotation: [0, 0, 0.5] },
    { geometry: new THREE.BoxGeometry(0.05, 0.26, 0.03), color: village.TIMBER,
      position: [0.32, 0.30, front + 0.005], rotation: [0, 0, -0.5] },
    // Steep street-facing gable roof (ridge along z) with eaves overhang.
    ...village.gableRoof({ cx: 0, cz: 0, topY: top, span: W, length: D, rise, axis: "z" }),
    // Two-step plaster attic gable filling the street end, plus a tiny
    // attic window.
    { geometry: new THREE.BoxGeometry(0.44, 0.16, 0.06), color: village.PLASTER,
      position: [0, 0.82, 0.31] },
    { geometry: new THREE.BoxGeometry(0.22, 0.16, 0.06), color: village.PLASTER,
      position: [0, 0.98, 0.31] },
    { geometry: new THREE.BoxGeometry(0.10, 0.12, 0.04), color: "#39444e",
      position: [0, 0.84, front + 0.005] },
    // Door under its own little post porch.
    { geometry: new THREE.BoxGeometry(0.20, 0.42, 0.05), color: DOOR,
      position: [-0.16, 0.35, front + 0.005] },
    { geometry: new THREE.BoxGeometry(0.04, 0.40, 0.04), color: village.TIMBER,
      position: [-0.28, 0.34, front + 0.18] },
    { geometry: new THREE.BoxGeometry(0.04, 0.40, 0.04), color: village.TIMBER,
      position: [-0.04, 0.34, front + 0.18] },
    { geometry: new THREE.BoxGeometry(0.32, 0.04, 0.26), color: "#7d4526",
      position: [-0.16, 0.60, front + 0.12], rotation: [0.45, 0, 0] },
    // Shuttered window beside the door.
    ...village.shutterWindow(0.18, 0.52, front + 0.005),
    // Tall brick chimney breaking through the back roof plane.
    { geometry: new THREE.BoxGeometry(0.13, 0.56, 0.13), color: BRICK,
      position: [0.22, 1.0, -0.16] },
    { geometry: new THREE.BoxGeometry(0.17, 0.05, 0.17), color: "#6e3a2c",
      position: [0.22, 1.305, -0.16] },
    ...plume.parts,
  ];
}

export const houseV2 = defineModel({
  id: "house-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "House (v2)",
  tags: ["building","house","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly house.",
  description: "A small wooden dwelling.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Hearth lit",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: plume.tracks,
    },
  ],
});
